import React from "react";

interface OutgoingMailsProps {
  title?: string;
  subtitle?: string;
  width?: number | string;
  height?: number | string;
}

const OutgoingMails: React.FC<OutgoingMailsProps> = ({
  title = "No outgoing mail",
  subtitle = "Sent messages will appear here",
  width = "100%",
  height = "auto",
}) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
      <svg
        width={width}
        height={height}
        viewBox="0 0 680 320"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        aria-label={title}
      >
        <title>{title}</title>
        <desc>Illustration of outgoing mail with envelopes and paper planes launching upward</desc>

        {/* Launch pad / tray */}
        <rect x="192" y="232" width="296" height="44" rx="6" fill="#e8e4f8" stroke="#c4baf0" strokeWidth="1.5" />
        <rect x="180" y="226" width="320" height="16" rx="5" fill="#c4baf0" />
        <rect x="180" y="224" width="14" height="56" rx="4" fill="#d4cef5" stroke="#c4baf0" strokeWidth="1" />
        <rect x="486" y="224" width="14" height="56" rx="4" fill="#d4cef5" stroke="#c4baf0" strokeWidth="1" />
        <rect x="192" y="268" width="296" height="10" rx="4" fill="#e0dbf5" stroke="#c4baf0" strokeWidth="1" />

        {/* Envelope on tray */}
        <rect x="212" y="238" width="256" height="28" rx="4" fill="#ffffff" stroke="#c4baf0" strokeWidth="1" />
        <polyline points="212,238 340,260 468,238" fill="none" stroke="#c4baf0" strokeWidth="1.2" />
        <line x1="212" y1="266" x2="288" y2="250" stroke="#c4baf0" strokeWidth="0.8" opacity="0.4" />
        <line x1="468" y1="266" x2="392" y2="250" stroke="#c4baf0" strokeWidth="0.8" opacity="0.4" />

        {/* Main paper plane launching right-upward */}
        <g transform="translate(308, 128) rotate(-32)">
          <polygon points="0,0 52,18 0,36" fill="#7c6fcd" />
          <polygon points="0,18 24,18 52,18 30,36 0,36" fill="#a99ee0" opacity="0.75" />
          <line x1="0" y1="18" x2="52" y2="18" stroke="#ffffff" strokeWidth="1.2" opacity="0.6" />
          {/* Trail lines */}
          <line
            x1="-8"
            y1="18"
            x2="-30"
            y2="18"
            stroke="#c4baf0"
            strokeWidth="1.8"
            strokeLinecap="round"
            opacity="0.45"
          />
          <line
            x1="-8"
            y1="24"
            x2="-22"
            y2="24"
            stroke="#c4baf0"
            strokeWidth="1.2"
            strokeLinecap="round"
            opacity="0.3"
          />
          <line
            x1="-8"
            y1="12"
            x2="-18"
            y2="12"
            stroke="#c4baf0"
            strokeWidth="1.2"
            strokeLinecap="round"
            opacity="0.3"
          />
        </g>

        {/* Small envelope floating upper-left */}
        <g transform="translate(175, 148) rotate(-5)">
          <rect
            x="0"
            y="0"
            width="110"
            height="36"
            rx="4"
            fill="#f0edfb"
            stroke="#b8b0e8"
            strokeWidth="1"
            opacity="0.8"
          />
          <polyline points="0,0 55,22 110,0" fill="none" stroke="#c4baf0" strokeWidth="1" opacity="0.8" />
          <line
            x1="14"
            y1="16"
            x2="60"
            y2="16"
            stroke="#c4baf0"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.35"
          />
          <line
            x1="14"
            y1="26"
            x2="48"
            y2="26"
            stroke="#c4baf0"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.22"
          />
        </g>

        {/* Small envelope upper-right */}
        <g transform="translate(400, 110) rotate(5)">
          <rect
            x="0"
            y="0"
            width="100"
            height="32"
            rx="4"
            fill="#ede9f8"
            stroke="#c4baf0"
            strokeWidth="1"
            opacity="0.7"
          />
          <polyline points="0,0 50,20 100,0" fill="none" stroke="#c4baf0" strokeWidth="1" opacity="0.7" />
        </g>

        {/* Up arrows indicating outgoing */}
        <g opacity="0.55">
          <line x1="340" y1="90" x2="340" y2="72" stroke="#7c6fcd" strokeWidth="2.5" strokeLinecap="round" />
          <polyline
            points="333,78 340,70 347,78"
            fill="none"
            stroke="#7c6fcd"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
        <g opacity="0.3">
          <line x1="312" y1="86" x2="312" y2="72" stroke="#7c6fcd" strokeWidth="2" strokeLinecap="round" />
          <polyline
            points="306,78 312,70 318,78"
            fill="none"
            stroke="#7c6fcd"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
        <g opacity="0.3">
          <line x1="368" y1="86" x2="368" y2="72" stroke="#7c6fcd" strokeWidth="2" strokeLinecap="round" />
          <polyline
            points="362,78 368,70 374,78"
            fill="none"
            stroke="#7c6fcd"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>

        {/* Dotted arc trail from tray to sky */}
        <path
          d="M 290 230 Q 260 180 300 130"
          fill="none"
          stroke="#c4baf0"
          strokeWidth="1.5"
          strokeDasharray="4 5"
          strokeLinecap="round"
          opacity="0.4"
        />

        {/* Decorative dots */}
        <circle cx="162" cy="200" r="3.5" fill="#c4baf0" opacity="0.4" />
        <circle cx="150" cy="216" r="2" fill="#c4baf0" opacity="0.28" />
        <circle cx="522" cy="192" r="3.5" fill="#c4baf0" opacity="0.4" />
        <circle cx="536" cy="208" r="2" fill="#c4baf0" opacity="0.28" />

        {/* Sparkles */}
        <path
          d="M464 100 L465.5 105 L471 106.5 L465.5 108 L464 113 L462.5 108 L457 106.5 L462.5 105 Z"
          fill="#a99ee0"
          opacity="0.5"
        />
        <path
          d="M214 118 L215 122 L219 123 L215 124 L214 128 L213 124 L209 123 L213 122 Z"
          fill="#a99ee0"
          opacity="0.4"
        />
        <circle cx="490" cy="148" r="2.5" fill="#c4baf0" opacity="0.35" />
        <circle cx="175" cy="130" r="2" fill="#c4baf0" opacity="0.3" />
      </svg>

      {(title || subtitle) && (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          {title && (
            <p style={{ margin: 0, fontSize: 15, color: "var(--color-text-secondary, #888)", fontWeight: 400 }}>
              {title}
            </p>
          )}
          {subtitle && (
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-tertiary, #aaa)", fontWeight: 400 }}>
              {subtitle}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default OutgoingMails;
